<script setup lang="ts">
import type { BackendError } from '~/types'

import { ref, computed } from 'vue'
import { whenever } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

import axios from 'axios'
import textClipper from 'text-clipper'

import Button from '~/ui/Button.vue'
import Alert from '~/ui/Alert.vue'
import Spacer from '~/ui/Spacer.vue'
import Modal from '~/ui/Modal.vue'
import SanitizedHtml from '~/ui/SanitizedHtml.vue'

interface Events {
  (e: 'updated', data: unknown): void
}

interface Props {
  content?: { text?: string, html?: string } | null
  fieldName?: string
  updateUrl?: string
  canUpdate?: boolean
  fetchHtml?: boolean
  permissive?: boolean
  truncateLength?: number
  moreLink?: boolean
  /**
   * When set, "more" opens a modal with this title showing the full
   * description instead of expanding in place. Use the `#modal-image` and
   * `#modal-after` slots to add page-specific content (cover art, external
   * links, ...) inside that modal.
   */
  modalTitle?: string
}

const { t } = useI18n()

const emit = defineEmits<Events>()
const props = withDefaults(defineProps<Props>(), {
  content: null,
  fieldName: 'description',
  updateUrl: '',
  canUpdate: true,
  fetchHtml: false,
  permissive: false,
  truncateLength: 200,
  moreLink: false,
  modalTitle: undefined
})

const showModal = ref(false)

const preview = ref('')
const fetchPreview = async () => {
  const response = await axios.post('text-preview/', { text: props.content?.text ?? '', permissive: props.permissive })
  preview.value = response.data.rendered
}

whenever(() => props.fetchHtml, fetchPreview)

// Interop with CJS
const clip = typeof textClipper === 'function' ? textClipper : (textClipper as { default: typeof textClipper }).default
const truncatedHtml = computed(() => clip(props.content?.html ?? '', props.truncateLength, {
  html: true,
  maxLines: 3
}))

const showMore = ref(false)

// Truncated or full Html or an empty string
const html = computed(() => props.fetchHtml
  ? preview.value
  : props.truncateLength > 0 && !showMore.value
    ? truncatedHtml.value
    : props.content?.html ?? ''
)

const isTruncated = computed(() => props.truncateLength > 0 && truncatedHtml.value.length < (props.content?.html ?? '').length)

const toggleMore = () => {
  if (props.modalTitle) {
    showModal.value = true
  } else {
    showMore.value = !showMore.value
  }
}

const isUpdating = ref(false)
const text = ref(props.content?.text ?? '')
const isLoading = ref(false)
const errors = ref([] as string[])
const submit = async () => {
  errors.value = []
  isLoading.value = true

  try {
    const response = await axios.patch(props.updateUrl, {
      [props.fieldName]: text.value
        ? { content_type: 'text/markdown', text: text.value }
        : null
    })

    emit('updated', response.data)
    isUpdating.value = false
  } catch (error) {
    errors.value = (error as BackendError).backendErrors
  }

  isLoading.value = false
}
</script>

<template>
  <!-- Render the truncated or full description -->
  <div
    v-if="content && !isUpdating"
    class="layout-row"
    ui-gap="8"
  >
    <SanitizedHtml
      :html
      ui-shrinkable
      :class="['description', isTruncated ? 'truncated' : '']"
    />

    <!-- Display the `show more` / `show less` button -->
    <span
      v-if="isTruncated && (moreLink || modalTitle)"
      :class="
      'more'
      /* Note: This is an anti-pattern. See ~/ui/button.md. Use HTML `<button>` instead to strip funkwhale-specific button styles.

      <!-- NOTE: A real <button>, not <Link> — this doesn't navigate, it toggles local
            state or opens a modal, so it shouldn't be announced as a link. Visually
            borrows the same link color/underline tokens fw.css reserves for `a[href]`. -->
      */
    "
    >
      <Button @click.stop.prevent="toggleMore">
        {{ !modalTitle && showMore
          ? t('components.common.RenderedDescription.button.less')
          : t('components.common.RenderedDescription.button.more') }}
      </Button>
    </span>
  </div>
  <span v-else-if="!isUpdating">
    {{ t('components.common.RenderedDescription.empty.noDescription') }}
  </span>

  <!-- [DISABLED] Display an edit form -->
  <!-- TODO: Check if we want to revive in-situ editing here -->

  <form
    v-if="isUpdating"
    @submit.prevent="submit()"
  >
    <Alert
      v-if="errors.length > 0"
      red
      title="{{ t('components.common.RenderedDescription.header.failure') }}"
      role="alert"
    >
      <ul class="list">
        <li
          v-for="(error, key) in errors"
          :key="key"
        >
          {{ error }}
        </li>
      </ul>
    </Alert>
    <content-form
      v-model="text"
      :autofocus="true"
    />
    <Button
      @click.prevent="isUpdating = false"
    >
      {{ t('components.common.RenderedDescription.button.cancel') }}
    </Button>
    <Spacer grow />
    <Button
      :aria-busy="isLoading"
      :disabled="isLoading"
      class="color-primary-solid"
    >
      {{ t('components.common.RenderedDescription.button.update') }}
    </Button>
  </form>

  <Modal
    v-if="modalTitle"
    v-model="showModal"
    :title="modalTitle"
  >
    <slot name="modal-image" />
    <Spacer />
    <SanitizedHtml :html="content?.html ?? ''" />
    <slot name="modal-after" />
  </Modal>
</template>

<style scoped>
:has(>.description) {
    position: relative;
    align-items: first baseline;
}

.description {
  display: flex; flex-direction: column;
}

.description.truncated {
    overflow: hidden;
    text-overflow: ellipsis;
}

.more :deep(button) {
  /* Note: This is an anti-pattern. See ~/ui/button.md. Use HTML `<button>` instead to strip funkwhale-specific button styles. */
  background: none;
  padding: 0;
  min-inline-size: 0;
  inline-size: auto;
  color: var(--color-link);
  text-decoration: underline;
  text-underline-offset: 4px;
  text-decoration-thickness: 2px;
  cursor: pointer;
  height: auto;
}

.more :deep(button:hover),
.more :deep(button:focus-visible) {
  color: var(--color-link-hover);
}
</style>
